// A LIVE preview of the destination route, rendered into the incoming pane while a finger drags.
//
// The router renders exactly ONE <Outlet/>. A gesture deliberately doesn't commit the URL until it
// completes, so during the drag the live tree is still the ORIGIN — which leaves nothing to put in the
// incoming pane but a frozen snapshot of the destination's last visit (or a skeleton the first time). A
// snapshot can never finish loading: hold the swipe halfway for ten seconds and it stays exactly as dead as
// it started. That's the gap this closes.
//
// Instead we mount a SECOND, client-only router pinned to the destination through a memory history. It's a
// real router: it runs the destination's loaders, resolves its chunk, and renders its pendingComponent then
// its real content. Because it shares the parent's QueryClient (via `context`), the chunk + data that
// `preloadRoute` warms on pointer-down land in THIS tree — so a half-completed gesture visibly fills in,
// like a native pager. The URL still only moves when the gesture completes.
//
// The shadow router walks the same route tree, so its __root would otherwise re-render the entire document
// shell (<html>, head, header, toaster) inside the pane. `previewFrom` in its context tells the root route to
// render a bare <Outlet/> instead — see routes/__root.tsx — and tells a NESTED surface's host layout (the
// admin console) to render only its own <Outlet/>, so a tab preview holds a tab and not a second copy of the
// console around it. Everything else the subtree needs (QueryClientProvider, I18nProvider, CallProvider) is
// inherited from the real tree, because this component renders inside it.

import { useEffect, useState } from 'react'
import { RouterProvider, createMemoryHistory, createRouter, useRouter } from '@tanstack/react-router'
import { routeTree } from '@/routeTree.gen'
import type { RoutePath } from './surface.config'

/**
 * Inside a live preview: the path of the surface being previewed FROM ('/' for the root outlet, '/admin' for
 * the admin tab strip), or `null` in the real app.
 *
 * A layout that hosts a NESTED transition surface must render only its `<Outlet/>` when this equals its own
 * path — otherwise the preview, which renders the route tree from the top, would repeat that layout's chrome
 * (the admin title + tab bar) inside the tab-sized pane it is supposed to fill.
 */
export function useTransitionPreviewFrom(): RoutePath | null {
  const context = useRouter().options.context as { previewFrom?: RoutePath }
  return context.previewFrom ?? null
}

/**
 * WHERE THIS COMPONENT STANDS relative to the surface being previewed — the question every layout above a
 * nested surface has to answer, asked once and answered by segment rather than by substring.
 *
 * `'live'`        — no preview anywhere. The real app.
 * `'passthrough'` — the previewed surface is THIS SCOPE or something below it, so this component is chrome
 *                   AROUND the pane: render only the subtree that leads to the surface (usually a bare
 *                   `<Outlet/>`, or the pager itself). Rendering the full layout here is what puts a second
 *                   copy of a section's title bar and tab strip inside the tab-sized pane it should fill.
 * `'above'`       — the previewed surface is an ANCESTOR of this scope: this subtree is ordinary content
 *                   inside someone else's pane, at rest. Usually render normally; a layout whose chrome only
 *                   makes sense at the top of a live tree (a stack's observers, a debug HUD) opts out here.
 * `'aside'`       — the preview is in an unrelated branch entirely, which means this subtree is not in it.
 *
 * BY SEGMENT, NEVER BY PREFIX STRING. `previewFrom.startsWith('/teams')` says yes to `/teams-archive/1`, and
 * — the case that actually bit — the ROOT surface previews with scope `'/'`, which is a string prefix of
 * everything, so every one of these gates had to remember its own `previewFrom !== '/'` guard. Here `'/'` is
 * simply ABOVE every scope, which is what it is.
 */
export type PreviewMode = 'live' | 'passthrough' | 'above' | 'aside'

export function usePreviewMode(scope: RoutePath): PreviewMode {
  const previewFrom = useTransitionPreviewFrom()
  if (previewFrom === null) return 'live'
  if (previewFrom === scope || isUnder(previewFrom, scope)) return 'passthrough'
  return isUnder(scope, previewFrom) ? 'above' : 'aside'
}

/** Is `path` strictly below `base`, counted in SEGMENTS — `/teams/42` is under `/teams`, `/teams-archive` isn't. */
function isUnder(path: RoutePath, base: RoutePath): boolean {
  if (base === '/') return path !== '/'
  return path.startsWith(base.endsWith('/') ? base : `${base}/`)
}

/**
 * NAVIGATIONS FORWARD TO THE REAL ROUTER. A preview's history is a memory one, so a navigation started inside
 * this tree would move the shadow router and the URL would never hear about it — the pane would show a page the
 * app is not on, and the pager would still be sitting on the member it was showing.
 *
 * This is what makes an interactive preview possible at all, and on the gesture engine's `interactiveIncoming`
 * surfaces it is now load-bearing rather than a guarantee held in reserve: a link tapped on the arriving page
 * mid-settle moves the REAL router, and the transition covering it resolves against that navigation like any
 * other (the outlet's onBeforeLoad sees it, and the controller re-arms or gets out of the way). On the scroll
 * engine the slots stay `inert` regardless — the browser's scroll owns every touch once it has claimed the pan,
 * so there is no interaction to forward. The INITIAL load is not a navigation and must not forward —
 * `fromLocation` is what tells them apart.
 */
export function RoutePreview(props: { to: RoutePath; previewFrom: RoutePath }) {
  const parent = useRouter()
  // Created once per mount; the host re-keys on `to`, so a different destination is a fresh router.
  const [previewRouter] = useState(() =>
    createRouter({
      routeTree,
      history: createMemoryHistory({ initialEntries: [props.to] }),
      context: { ...parent.options.context, previewFrom: props.previewFrom },
      // This tree IS the preload of the destination — it must never fan out preloads of its own (every Link
      // it renders would otherwise warm a route nobody asked for).
      defaultPreload: false,
      defaultPreloadStaleTime: 0,
      // Show the destination's own `pendingComponent` from the FIRST frame of the slide. This is a FRESH
      // router doing an INITIAL load (not a navigation between already-rendered routes like the real app), and
      // for that path the pending UI is gated by the router's pending timers, NOT Suspense — with the defaults
      // (`defaultPendingMs` 1000ms) a fresh router renders NOTHING until its first load fully resolves, so on a
      // slow network the pane sat blank for the whole slide and the skeleton only appeared once the real outlet
      // took over at the reveal (device-video report). Zeroed here so the route's skeleton paints immediately
      // while its chunk + loader resolve, then fills in — matching what a real link navigation shows. (Warm
      // cache is unaffected: nothing pends, so there's no pending window to time.)
      defaultPendingMs: 0,
      defaultPendingMinMs: 0,
      // No skeleton of our OWN — the route declares its `pendingComponent`; a route without one shows nothing,
      // exactly as on a real navigation.
      defaultPendingComponent: () => null,
      // A preview that throws is not worth surfacing: the pane stays blank, and there is nothing in it to
      // interact with even where the pane is interactive. If the failure is real, the actual navigation on
      // commit runs its own loaders and shows the real error.
      defaultErrorComponent: () => null,
    }),
  )
  // Subscribed once: `previewRouter` is created once per mount and `parent` never changes identity.
  useEffect(
    () =>
      previewRouter.subscribe('onBeforeNavigate', (event) => {
        const to = event.toLocation.href
        // The initial load carries no `fromLocation`; a real navigation does. And nothing to forward if it is
        // going where the app already is.
        if (!event.fromLocation || to === parent.state.location.href) return
        void parent.navigate({ to })
      }),
    [previewRouter, parent],
  )
  return <RouterProvider router={previewRouter} />
}
